import React, { useState, useEffect, useCallback } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  AbilityEffect, 
  AttachedTable, 
  Condition, 
  CreatureLimitation, 
  Potence,
  RollableTable,
  UpgradableNumber,
} from "@dndModels";
import { 
  ABILITY_SCORES_EXPANDED,
  AREA_TYPES,
  ATTACK_TYPES,
  DAMAGE_TYPES,
  EFFECT_TYPES,
  TARGET_TYPES
} from "@dndModels/Constants";
import { 
  AbilityScoreExpandedType, 
  AreaType, 
  AttackType, 
  DamageType, 
  EffectType, 
  PotenceType, 
  TargetType 
} from "@dndModels/Types";
import { DisablableOption, FeatureSource, ModelBase } from "@models";

import DandDAPI from "@dndUtilities/system_api";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox"; 
import SelectObjectBox from "@components/input/selects/SelectObjectBox"; 
import CheckBox from "@components/input/CheckBox";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";

import AbilityPotenceInput from "@dndComponents/feature/AbilityPotenceInput"; 
import AbilityPotenceCreaturesInput from "@dndComponents/feature/AbilityPotenceCreatures"; 
import AbilityPotenceCreateResourceOptionInput from "@dndComponents/feature/AbilityPotenceCreateResourceOptionInput"; 
import RollableTableInput from "@dndComponents/other/RollableTableInput";
import AbilityPotenceTransformInput from "@dndComponents/feature/AbilityPotenceTransformInput";
// import AbilityPotenceAdaptationInput from './AbilityPotenceAdaptationInput';
import AbilityPotenceCreateItemInput from "@dndComponents/feature/AbilityPotenceCreateItemInput";
import AbilityPotenceEnhanceItemInput from "@dndComponents/feature/AbilityPotenceEnhanceItemInput";
import AbilityPotenceSelfConditionsInput from "@dndComponents/feature/AbilityPotenceSelfConditionsInput";
import AbilityPotenceSelfGiftsInput from "@dndComponents/feature/AbilityPotenceSelfGiftsInput";
import DeleteButton from "@components/input/buttons/DeleteButton";
import AbilityPotenceControlInput from "@dndComponents/feature/AbilityPotenceControlInput";
import AddButton from "@components/input/buttons/AddButton";
import LoadingBox from "@components/input/LoadingBox";
import AbilityPotenceEnhanceCreatureInput from './AbilityPotenceEnhanceCreatureInput';

interface Props {
  obj: AbilityEffect;
  onChange: () => void;
  onDelete: () => void;
  slot_level?: number;
  disabled: boolean;
  for_obj?: ModelBase; 
  feature_source?: FeatureSource;
  attached_table?: AttachedTable;
  min_potence?: number;
  max_potence?: number;
}

const api = DandDAPI.getInstance();

export default function AbilityEffectInput({
  obj,
  onChange,
  onDelete,
  slot_level = -1,
  disabled,
  for_obj,
  feature_source,
  attached_table,
  min_potence = -1,
  max_potence = -1
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [sf_features, setSFFeatures] = useState<{ fb_id: string, f_id: string, ff_id: string }[]>([]);
  const [sf_options, setSFOptions] = useState<DisablableOption[]>([]);
  
  const load = useCallback(async () => {
    const res = await api.getSetOfObjects({ data_types: ["condition"] });
    if (res) {
      setConditions(res.condition as Condition[]);
    }
    if (obj.type === "Special Feature" && feature_source) {
      // Search for features that are SFFeatures
      const new_sf_features: { fb_id: string, f_id: string, ff_id: string }[] = [];
      const new_sf_options: DisablableOption[] = [];
      const fbs = feature_source.feature_bases.filter(o => o.feature_types.includes("Special Feature"));
      for (const fb of fbs) {
        const fs = fb.features.filter(o => o.feature_type === "Special Feature");
        for (const f of fs) {
          new_sf_features.push({ f_id: f.true_id, fb_id: fb.true_id, ff_id: f.floating_feature_id });
          new_sf_options.push({ id: f.true_id, text: f.name });
        }
      }
      setSFFeatures(new_sf_features);
      setSFOptions(new_sf_options);
    }
    setLoading(false);
  }, [obj, feature_source]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderTargetInt = () => {
    const target_int = obj.target_int;
    return (
      <Grid size={12}
        container spacing={0} direction="row">
        <Grid size={6}>
          <UpgradableNumberBox 
            name="Range"
            value={target_int.range} 
            disabled={disabled}
            attached_table={attached_table}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.target_int.range = changed.clone();
              onChange();
            }}
          />
        </Grid>
        <Grid size={6}>
          { !["Save","None"].includes(obj.attack_type) &&
            <UpgradableNumberBox 
              name="Range 2"
              value={target_int.range2} 
              disabled={disabled}
              attached_table={attached_table}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.target_int.range2 = changed.clone();
                onChange();
              }}
            />
          }
        </Grid>
        { ["Save", "None"].includes(obj.attack_type) && 
          <>
            <Grid size={6}>
              <SelectStringBox 
                name="Target Type"
                value={target_int.target_type} 
                options={TARGET_TYPES}
                disabled={disabled}
                onChange={(changed: TargetType) => {
                  const new_obj = obj;
                  new_obj.target_int.target_type = changed;
                  onChange();
                }}
              />
            </Grid>
            { target_int.target_type === "Area" &&
              renderAreaTypeOptions()
            }
          </>
        }
      </Grid>
    );
  }

  const renderAreaTypeOptions = () => {
    const target_int = obj.target_int;
    let area_name = "Radius";
    let area2_name = "";
    switch (target_int.area_type) {
      // case "Circle": case "Sphere": case "Cone": 
      // break;
      case "Cube": case "Square":
        area_name = "Width"; 
      break;
      case "Cylinder":
        area_name = "Radius"; 
        area2_name = "Height";
      break;
      case "Line":
        area_name = "Length"; 
        area2_name = "Width";
      break;
      case "Wall":
        area_name = "Length"; 
        area2_name = "Height";
      break;
    }
    return (
      <>
        <Grid
          size={{
            xs: 6
          }}>
          <SelectStringBox 
            name="Area Type"
            value={target_int.area_type} 
            options={AREA_TYPES}
            disabled={disabled}
            onChange={(changed: AreaType) => {
              const new_obj = obj;
              new_obj.target_int.area_type = changed;
              onChange();
            }}
          />
        </Grid>
        { target_int.area_type === "Wall" &&
          <>
            <Grid size={12} className="header">
              Flat Wall
            </Grid>
          </>
        }
        <Grid
          size={{
            xs: target_int.area_type === "Cylinder" || target_int.area_type === "Line" ? 6 : (target_int.area_type === "Wall" ? 4 : 12)
          }}>
          <UpgradableNumberBox 
            name={area_name}
            value={target_int.area} 
            disabled={disabled}
            attached_table={attached_table}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.target_int.area = changed.clone();
              onChange();
            }}
          />
        </Grid>
        { (target_int.area_type === "Cylinder" || target_int.area_type === "Line" || target_int.area_type === "Wall") && 
          <Grid
            size={{
              xs: target_int.area_type === "Wall" ? 4 : 6
            }}>
            <UpgradableNumberBox 
              name={area2_name}
              value={target_int.area2} 
              disabled={disabled}
              attached_table={attached_table}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.target_int.area2 = changed.clone();
                onChange();
              }}
            />
          </Grid>
        }
        { target_int.area_type === "Wall" &&
          <Grid size={4}>
            <UpgradableNumberBox 
              name="Thick"
              value={target_int.area3} 
              disabled={disabled}
              attached_table={attached_table}
              onChange={(changed: UpgradableNumber) => {
                const new_obj = obj;
                new_obj.target_int.area3 = changed.clone();
                onChange();
              }}
            />
          </Grid>
        }
        { target_int.area_type === "Wall" &&
          <>
            <Grid size={12} className="header">
              Ringed Wall
            </Grid>
            <Grid size={4}>
              <UpgradableNumberBox 
                name="Diameter"
                value={target_int.area4} 
                disabled={disabled}
                attached_table={attached_table}
                onChange={(changed: UpgradableNumber) => {
                  const new_obj = obj;
                  new_obj.target_int.area4 = changed.clone();
                  onChange();
                }}
              />
            </Grid>
            <Grid size={4}>
              <UpgradableNumberBox 
                name="Height"
                value={target_int.area5} 
                disabled={disabled}
                attached_table={attached_table}
                onChange={(changed: UpgradableNumber) => {
                  const new_obj = obj;
                  new_obj.target_int.area5 = changed.clone();
                  onChange();
                }}
              />
            </Grid>
            <Grid size={4}>
              <UpgradableNumberBox 
                name="Thick"
                value={target_int.area6} 
                disabled={disabled}
                attached_table={attached_table}
                onChange={(changed: UpgradableNumber) => {
                  const new_obj = obj;
                  new_obj.target_int.area6 = changed.clone();
                  onChange();
                }}
              />
            </Grid>
          </>
        }
      </>
    );
  }

  const renderSpecialFeatureEffect = () => {
    // console.log(feature_source);
    if (obj.type === "Special Feature") {
      if (sf_options.length > 0 && sf_features.length > 0) {
        return (
          <Grid size={12}>
            <SelectStringBox
              name="Special Feature"
              options={sf_options}
              value={obj.sf_feature.f_id}
              disabled={disabled}
              onChange={(changed: string) => {
                const new_obj = obj;
                const sf_feature = sf_features.find(o => o.f_id === changed);
                if (sf_feature) {
                  new_obj.sf_feature = sf_feature;
                }
                onChange();
              }}
            />
          </Grid>
        );
      } else if (feature_source instanceof FeatureSource) {
        return (
          <Grid size={12}>There aren't any Special Feature's on this Feature Source.</Grid>
        );
      } else {
        return (
          <Grid size={12}>This Feature Type hasn't been implemented for this type of object.</Grid>
        );
      }
    }
    return null;
  }

  const potenceChanged = (changed: Potence) => {
    const new_obj = obj;
    const potence = new_obj.potences.find(p => p.true_id === changed.true_id);
    if (potence) {
      potence.copy(changed, true);
    }
    onChange();
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    return (
      <Grid size={12}
        container spacing={0} direction="row">
        <Grid size={12}>
          { !disabled &&
            <DeleteButton
              onClick={ () => {
                onDelete();
              }}
            />
          }
          <SelectStringBox 
            options={[...EFFECT_TYPES,"Use Extra"]}
            value={obj.type} 
            name="Effect"
            disabled={disabled}
            onChange={(changed: EffectType) => {
              const new_obj = obj;
              new_obj.type = changed;
              new_obj.damage_types = [];
              if (["None","Self Condition","Summon","Portal","Transform","Adaptation","Create Resource","Draw Card"].includes(changed)) {
                new_obj.attack_type = "None";
              } else if (obj.type === "Special Feature" && feature_source instanceof FeatureSource) {
                // Search for features that are SFFeatures
                const new_sf_features: { fb_id: string, f_id: string, ff_id: string }[] = [];
                const new_sf_options: DisablableOption[] = [];
                const fbs = feature_source.feature_bases.filter(o => o.feature_types.includes("Special Feature"));
                for (const fb of fbs) {
                  const fs = fb.features.filter(o => o.feature_type === "Special Feature");
                  for (const f of fs) {
                    new_sf_features.push({ f_id: f.true_id, fb_id: fb.true_id, ff_id: f.floating_feature_id });
                    new_sf_options.push({ id: f.true_id, text: f.name });
                  }
                }
                setSFFeatures(new_sf_features);
                setSFOptions(new_sf_options);
              }
              new_obj.potences = [];
              onChange();
            }}
          />
        </Grid>
        { obj.type === "Damage" &&
          <Grid size={12}>
            <SelectStringBox
              name="Detail"
              options={DAMAGE_TYPES}
              values={obj.damage_types}
              disabled={disabled}
              multiple
              onChange={(changed: DamageType[]) => {
                const new_obj = obj;
                new_obj.damage_types = changed;
                onChange();
              }}
            />
          </Grid>
        }
        { obj.type === "Draw Card" &&
          <Grid size={12}>
            <CheckBox
              name="Choose"
              value={obj.choose_card}
              onChange={(changed: boolean) => {
                const new_obj = obj;
                new_obj.choose_card = changed;
                onChange();
              }}
            />
          </Grid>
        }
        { obj.type !== "Draw Card" && renderTargetInt() }
        { obj.type === "Selectable Key" &&
          <>
            <Grid size={12}>
              <SelectStringBox 
                value={obj.selectable_type} 
                name="Selectable Type"
                options={["Damage Type"]}
                disabled={disabled}
                onChange={(changed: string) => {
                  const new_obj = obj;
                  new_obj.selectable_type = changed;
                  onChange();
                }}
              />
            </Grid>
            <Grid size={12}>
              <StringBox 
                value={obj.selectable_key} 
                name="Selectable Key"
                disabled={disabled}
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  new_obj.selectable_key = changed;
                  onChange();
                }}
              />
            </Grid>
          </>
        }
        { obj.type === "Rollable Table" &&
          <>
            <Grid size={12}>
              { !reloading &&
                <SelectObjectBox
                  name="Rollable Table"
                  data_type="rollable_table"
                  extra_options={["New"]}
                  value={obj.rollable_table_id}
                  onChange={(changed: string) => {
                    const new_obj = obj;
                    new_obj.rollable_table_id = changed;
                    onChange();
                  }}
                />
              }
            </Grid>
            <Grid size={12}>
              <RollableTableInput 
                rollable_table_id={obj.rollable_table_id}
                onChange={(changed: RollableTable) => {
                  const new_obj = obj;
                  if (new_obj.rollable_table_id !== changed._id) {
                    new_obj.rollable_table_id = changed._id;
                    onChange();
                    setReloading(true);
                  }
                }}
              />
            </Grid>
          </>
        }
        { !["None","Control","Teleport","Portal","Draw Card"].includes(obj.type) &&
          <>
            <Grid size={{ xs: obj.potence_type === "Source" ? 6 : 12 }}>
              <SelectStringBox 
                name="Potence Type"
                options={["Slot","Character","Source","None"]}
                value={obj.potence_type} 
                disabled={disabled}
                onChange={(changed: PotenceType) => {
                  const new_obj = obj;
                  new_obj.potence_type = changed;
                  if (changed === "Source") {
                    new_obj.potence_class = "Default";
                  }
                  onChange();
                }}
              />
            </Grid>
            { obj.potence_type === "Source" && 
              <Grid size={6}>
                <SelectObjectBox
                  name="Potence Class"
                  data_type="game_class"
                  extra_options={["Default"]}
                  value={obj.potence_class}
                  disabled={disabled}
                  dominant_only
                  onChange={(changed: string) => {
                    const new_obj = obj;
                    new_obj.potence_class = changed;
                    onChange();
                  }}
                />
              </Grid>
            }
          </>
        }
        { !["None","Self Condition","Summon","Transform","Adaptation","Create Resource","Draw Card"].includes(obj.type) &&
          <Grid size={12}>
            <SelectStringBox 
              name="Attack Type"
              options={[...ATTACK_TYPES,"None"]}
              value={obj.attack_type} 
              disabled={disabled}
              onChange={(changed: AttackType) => {
                const new_obj = obj;
                new_obj.attack_type = changed;
                if (!new_obj.saving_throw_ability_score) {
                  new_obj.saving_throw_ability_score = "";
                }
                onChange();
              }}
            />
          </Grid>
        }
        { obj.attack_type === "Save" &&
          <Grid size={12}>
            <SelectStringBox 
              name="Saving Throw"
              options={ABILITY_SCORES_EXPANDED}
              value={obj.saving_throw_ability_score}
              disabled={disabled}
              onChange={(changed: AbilityScoreExpandedType) => {
                const new_obj = obj;
                new_obj.saving_throw_ability_score = changed;
                onChange();
              }}
            /> 
          </Grid>
        }
        { obj.type === "Damage" &&
          <Grid size={12}>
            <CheckBox
              name="1/2 on Success"
              value={ obj.half_on_success }
              disabled={disabled}
              onChange={(changed: boolean) => {
                const new_obj = obj;
                new_obj.half_on_success = changed;
                onChange();
              }}
            />
          </Grid>
        }
        { obj.type === "Create Resource" &&
          <Grid size={12}>
            <CheckBox 
              name="Refresh Only"
              value={ obj.refresh_only }
              disabled={disabled}
              onChange={(changed: boolean) => {
                const new_obj = obj;
                new_obj.refresh_only = changed;
                onChange();
              }}
            /> 
          </Grid>
        }
        { !["None","Teleport","Portal","Draw Card","Special Feature"].includes(obj.type) &&
          <Grid size={12}
            container spacing={0} direction="row">
            <Grid size={12}>
              <span className="header padded">
                Potences
              </span>
              { !disabled &&
                <AddButton
                  disabled={disabled}
                  onClick={ () => {
                    const new_obj = obj;
                    const potence = new Potence();
                    if (new_obj.type === "Enhance Creature") {
                      potence.limits = new CreatureLimitation();
                    }
                    if (new_obj.potences.length > 0) {
                      const last = new_obj.potences[new_obj.potences.length - 1];
                      potence.copy(last, true);
                      potence.level = last.level + 1;
                      if (obj.type === "Self Condition") {
                        if (potence.conditions_applied.length === 1) {
                          const condition = conditions.find(o => o._id === potence.conditions_applied[0]);
                          if (condition instanceof Condition && condition.max_level > 1 &&
                            potence.condition_levels_applied.isBasic() && 
                            potence.condition_levels_applied.base > 0) {
                            potence.condition_levels_applied.base++;
                          }
                        }
                      } else if (potence.rolls.count.isBasic() && potence.rolls.count.base > 0) {
                        potence.rolls.count.base++;
                      }
                    } else if (min_potence > -1) {
                      potence.level = min_potence;
                    }
                    new_obj.potences.push(potence);
                    onChange();
                    setReloading(true);
                  }}
                />
              }
            </Grid>
            { !reloading && obj.potences.map(p => {
              if (obj.type === "Summon") {
                return (
                  <Grid size={12} key={p.true_id}>
                    <AbilityPotenceCreaturesInput
                      obj={p}
                      slot_level={slot_level}
                      disabled={disabled}
                      onChange={potenceChanged}
                      onDelete={() => {
                        const new_obj = obj;
                        new_obj.potences = new_obj.potences.filter(o => o.true_id !== p.true_id);
                        onChange();
                      }}
                    />
                  </Grid>
                );
              } else if (obj.type === "Create Resource") {
                return (
                  <Grid size={12} key={p.true_id}>
                    <AbilityPotenceCreateResourceOptionInput
                      obj={p}
                      slot_level={slot_level}
                      disabled={disabled}
                      onChange={potenceChanged}
                      onDelete={() => {
                        const new_obj = obj;
                        new_obj.potences = new_obj.potences.filter(o => o.true_id !== p.true_id);
                        onChange();
                      }}
                    />
                  </Grid>
                );
              } else if (obj.type === "Create Item") {
                return (
                  <Grid size={12} key={p.true_id}>
                    <AbilityPotenceCreateItemInput
                      effect={obj}
                      obj={p}
                      disabled={disabled}
                      onChange={potenceChanged}
                      onEffectChange={() => { // changed: AbilityEffect
                        // const new_obj = obj;
                        // new_obj.copy(changed);
                        onChange();
                      }}
                      onDelete={() => {
                        const new_obj = obj;
                        new_obj.potences = new_obj.potences.filter(o => o.true_id !== p.true_id);
                        onChange();
                      }}
                    />
                  </Grid>
                );
              } else if (obj.type === "Enhance Item") {
                return (
                  <Grid size={12} key={p.true_id}>
                    <AbilityPotenceEnhanceItemInput
                      obj={p}
                      disabled={disabled}
                      onChange={potenceChanged}
                      onDelete={() => {
                        const new_obj = obj;
                        new_obj.potences = new_obj.potences.filter(o => o.true_id !== p.true_id);
                        onChange();
                      }}
                    />
                  </Grid>
                );
              } else if (obj.type === "Enhance Creature") {
                return (
                  <Grid size={12} key={p.true_id}>
                    <AbilityPotenceEnhanceCreatureInput
                      obj={p}
                      disabled={disabled}
                      onChange={potenceChanged}
                      onDelete={() => {
                        const new_obj = obj;
                        new_obj.potences = new_obj.potences.filter(o => o.true_id !== p.true_id);
                        onChange();
                      }}
                    />
                  </Grid>
                );
              } else if (obj.type === "Transform") {
                return (
                  <Grid size={12} key={p.true_id}>
                    <AbilityPotenceTransformInput
                      obj={p}
                      slot_level={slot_level}
                      disabled={disabled}
                      onChange={potenceChanged}
                      onDelete={() => {
                        const new_obj = obj;
                        new_obj.potences = new_obj.potences.filter(o => o.true_id !== p.true_id);
                        onChange();
                      }}
                    />
                  </Grid>
                );
              } else if (obj.type === "Self Condition") {
                return (
                  <Grid size={12} key={p.true_id}>
                    <AbilityPotenceSelfConditionsInput
                      obj={p}
                      slot_level={slot_level}
                      disabled={disabled}
                      for_obj={for_obj}
                      onChange={potenceChanged}
                      onDelete={() => {
                        const new_obj = obj;
                        new_obj.potences = new_obj.potences.filter(o => o.true_id !== p.true_id);
                        onChange();
                      }}
                    />
                  </Grid>
                );
              } else if (obj.type === "Self Gift") {
                return (
                  <Grid size={12} key={p.true_id}>
                    <AbilityPotenceSelfGiftsInput
                      obj={p}
                      slot_level={slot_level}
                      disabled={disabled}
                      add_new_data={for_obj}
                      onChange={potenceChanged}
                      onDelete={() => {
                        const new_obj = obj;
                        new_obj.potences = new_obj.potences.filter(o => o.true_id !== p.true_id);
                        onChange();
                      }}
                    />
                  </Grid>
                );
              } else if (obj.type === "Control" || obj.type === "Utility") {
                return (
                  <AbilityPotenceControlInput key={p.true_id}
                    obj={p}
                    disabled={disabled}
                    onChange={() => {
                      // const new_obj = obj;
                      // const objFinder = new_obj.potences.filter(p => p.true_id === changed.true_id);
                      // if (objFinder.length === 1) {
                      //   const potence = objFinder[0];
                      //   potence.level = changed.level;
                      //   potence.rolls.count = changed.rolls.count.clone();
                      //   potence.rolls.size = changed.rolls.size.clone();
                      //   potence.rolls.flat = changed.rolls.flat.clone();
                      // }
                      onChange();
                    }}
                    onDelete={() => {
                      const new_obj = obj;
                      new_obj.potences = new_obj.potences.filter(o => o.true_id !== p.true_id);
                      onChange();
                    }}
                  />
                );
              } else {
                return (
                  <AbilityPotenceInput key={p.true_id}
                    obj={p}
                    disabled={disabled}
                    attached_table={attached_table}
                    min_potence={min_potence}
                    max_potence={max_potence}
                    onChange={potenceChanged}
                    onDelete={() => {
                      const new_obj = obj;
                      new_obj.potences = new_obj.potences.filter(o => o.true_id !== p.true_id);
                      onChange();
                    }}
                  />
                );
              }
            })}
          </Grid>
        }
        { ["Utility","Control","Teleport","Portal"].includes(obj.type) &&
          <Grid size={12}>
            <StringBox
              name="Detail"
              value={obj.utility_detail}
              disabled={disabled}
              onBlur={(changed: string) => {
                const new_obj = obj;
                new_obj.utility_detail = changed;
                onChange();
              }}
            />
          </Grid>
        }
        { renderSpecialFeatureEffect() }
        <Grid size={12}>
          <StringBox 
            name="Effect Name"
            value={obj.name}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.name = changed;
              onChange();
            }}
          />
        </Grid>
        <Grid size={12}>
          <StringBox 
            name="Description"
            value={obj.description}
            multiline
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.description = changed;
              onChange();
            }}
          />
        </Grid>
      </Grid>
    );
  }
}
